import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-playlist';
import 'videojs-playlist-ui';
import 'videojs-playlist-ui/dist/videojs-playlist-ui.css';

const ImaPlayer = () => {
  const videoRef = useRef(null);
  const adContainerRef = useRef(null);
  const playerRef = useRef(null);
  const adsLoaderRef = useRef(null);
  const adsManagerRef = useRef(null);
  const upcomingItemRef = useRef(null); // Ref to store upcoming playlist item
  const [isAdPlaying, setIsAdPlaying] = useState(false);

  const [playlist] = useState([
    {
      id: 0,
      name: '2010 Year-End Podcast Inserts',
      sources: [{ src: 'https://cdn.iflmedia.com/mp3/socialmedia/201112-christmas-wishes/01-Charlie-Costa.mp3', type: 'audio/mp3' }],
      poster: 'https://cdn.iflmedia.com/images/ifl-usa/v195/pastors-2023/Charlie.jpg',
      prerollAdUrl: 'https://insight.org/media/xml/vast-test.php?media=1',
    },
    {
      id: 1,
      name: 'Compelled to Proclaim the Messiah and His Message',
      sources: [{ src: 'https://cdn.iflmedia.com/mp3/advancement/2013-compelled-invitation.mp3', type: 'audio/mp3' }],
      poster: 'https://cdn.iflmedia.com/images/ifl-usa/chuck-swindoll-international.png',
      prerollAdUrl: 'https://insight.org/media/xml/vast-test.php?media=2',
    },
  ]);

  // Function to clean up AdsManager
  const cleanupAdsManager = () => {
    if (adsManagerRef.current) {
      adsManagerRef.current.destroy();
      adsManagerRef.current = null;
      console.log('IMA: AdsManager destroyed');
    }
  };

  // Function to handle ad errors
  const onAdError = (adErrorEvent) => {
    console.error('IMA: Ad Error:', adErrorEvent.getError());
    cleanupAdsManager();
    setIsAdPlaying(false);
    playerRef.current?.play(); // Resume content on ad error
  };

  // Function to handle loaded AdsManager
  const onAdsManagerLoaded = (adsManagerLoadedEvent) => {
    console.log('IMA: AdsManager loaded');
    const adsManager = adsManagerLoadedEvent.getAdsManager(videoRef.current);
    adsManagerRef.current = adsManager;

    adsManager.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);
    adsManager.addEventListener(window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
      console.log('IMA: Content pause requested');
      setIsAdPlaying(true);
      playerRef.current?.pause();
    });
    adsManager.addEventListener(window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
      console.log('IMA: Content resume requested');
      setIsAdPlaying(false);
      
      // Set poster AFTER ad is done
      const upcomingItem = upcomingItemRef.current;
      if (playerRef.current && upcomingItem && upcomingItem.poster) {
        console.log('Player: Setting poster after ad to:', upcomingItem.poster);
        playerRef.current.poster(upcomingItem.poster);
      }

      playerRef.current?.play();
    });
    adsManager.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
        console.log('IMA: All ads completed');
        cleanupAdsManager();
    });

    try {
      const { clientWidth, clientHeight } = videoRef.current.parentElement;
      adsManager.init(clientWidth, clientHeight, window.google.ima.ViewMode.NORMAL);
      adsManager.start();
      console.log('IMA: AdsManager started');
    } catch (adError) {
      console.error('IMA: AdsManager could not be started:', adError);
      onAdError({ getError: () => adError });
    }
  };

  // Main setup effect
  useEffect(() => {
    // Ensure IMA SDK is loaded
    if (!window.google || !window.google.ima) {
      console.error('IMA: Google IMA SDK not found');
      return;
    }

    // 1. Create Video.js player
    const player = videojs(videoRef.current, {
      controls: true,
      fluid: true,
    });
    playerRef.current = player;

    // 2. Initialize Playlist
    player.playlist(playlist);
    player.playlist.autoadvance(0);
    player.playlistUi();

    // 3. IMA SDK Setup
    const adDisplayContainer = new window.google.ima.AdDisplayContainer(adContainerRef.current, videoRef.current);
    // adDisplayContainer.initialize(); // Must be called on user gesture - moved to first play
    const adsLoader = new window.google.ima.AdsLoader(adDisplayContainer);
    adsLoaderRef.current = adsLoader;

    // 4. AdsLoader event listeners
    adsLoader.addEventListener(window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAdsManagerLoaded, false);
    adsLoader.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);

    // 5. Function to request ads
    const requestAds = (adTagUrl) => {
      cleanupAdsManager(); // Clean up previous ads manager
      console.log('IMA: Requesting ads with tag:', adTagUrl);
      const adsRequest = new window.google.ima.AdsRequest();
      adsRequest.adTagUrl = adTagUrl;
      adsRequest.linearAdSlotWidth = player.el().clientWidth;
      adsRequest.linearAdSlotHeight = player.el().clientHeight;
      adsRequest.nonLinearAdSlotWidth = player.el().clientWidth;
      adsRequest.nonLinearAdSlotHeight = player.el().clientHeight / 3;
      adsLoaderRef.current.requestAds(adsRequest);
    };

    // 6. Listen for playlist changes to trigger ads
    const handleBeforePlaylistItem = (event, item) => {
      console.log('Player: beforeplaylistitem event triggered for:', item.name);
      upcomingItemRef.current = item; // Store the upcoming item

      if (item.prerollAdUrl) {
        requestAds(item.prerollAdUrl);
      } else {
        console.log('Player: No preroll ad for this item. Setting poster directly.');
        // If no ad, set poster immediately since there's no ad conflict
        if (playerRef.current && item.poster) {
            playerRef.current.poster(item.poster);
        }
      }
    };
    player.on('beforeplaylistitem', handleBeforePlaylistItem);

    // 7. Set initial poster and handle first play to trigger ad
    if (playlist.length > 0) {
        const firstItem = playlist[0];
        player.poster(firstItem.poster); // Set poster for the first item
        upcomingItemRef.current = firstItem;

        const handleFirstPlay = (event) => {
            console.log('Player: First play event');
            // Initialize ad container on first user interaction
            adDisplayContainer.initialize();

            // Prevent the content from playing if there's a preroll
            if (firstItem.prerollAdUrl) {
                event.preventDefault();
                console.log('Player: First play detected, requesting initial ad.');
                requestAds(firstItem.prerollAdUrl);
                // The 'play' listener is removed by .one() automatically after execution
            }
        };

        player.one('play', handleFirstPlay);
    }

    // 8. Cleanup on component unmount
    return () => {
      cleanupAdsManager();
      if (adsLoaderRef.current) {
        adsLoaderRef.current.removeEventListener(window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAdsManagerLoaded);
        adsLoaderRef.current.removeEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);
        adsLoaderRef.current.destroy();
        adsLoaderRef.current = null;
      }
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playlist]); // Re-run if playlist changes

  return (
    <div style={{ display: 'flex', gap: '20px', maxWidth: '1000px', margin: 'auto' }}>
      <div style={{ flex: '2 1 640px', position: 'relative' }}>
        <div ref={adContainerRef} style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: isAdPlaying ? 'block' : 'none', // Show only during ad playback
        }} />
        <audio id="content_video" ref={videoRef} className="video-js vjs-default-skin" playsInline />
      </div>
      <div style={{ flex: '1 1 300px' }}>
        {/* The playlist UI will be rendered here by the plugin */}
        {playlist.length > 1 && <div className="vjs-playlist playlist-layout" />}
      </div>
    </div>
  );
};

export default ImaPlayer;